import React, { useState, useEffect, useCallback } from 'react';
import { topManagementApi, ataItemsApi } from '../api';
import { toast } from 'sonner';
import { validateDateFields, extractApiError, formatDateBR } from '../utils/dateValidation';
import { Crown, NotebookText, Plus, Edit, Trash2, X, Loader2, Eye, Users, Send, CheckCircle2, Clock, Download } from 'lucide-react';
import AtaItems from './AtaItems';

const FORM_CODE = 'F-017';
const FORM_REVISION = '01';
const FORM_REF = 'MGQ / PGQ 002';

// Pauta (a-o) + Ações (p-w) — the full lettered agenda of the printed form.
// The "Tendências" sub-table is nested right after topic "e" — its rows are
// picked from the Itens de ATA catalog (category "tendencia"), not fixed.
const PAUTA_TOPICS = [
  { key: 'a', label: 'Resultados das Atas Anteriores' },
  { key: 'b', label: 'Mudanças' },
  { key: 'c', label: 'Satisfação do Cliente e das partes interessadas' },
  { key: 'd', label: 'Planejamento, Indicadores e Objetivos' },
  { key: 'e', label: 'Tendências' },
  { key: 'f', label: 'Desempenho do Processo e Conformidade do Produto' },
  { key: 'g', label: 'SACPM' },
  { key: 'h', label: 'Desempenho dos fornecedores' },
  { key: 'i', label: 'Recursos' },
  { key: 'j', label: 'Análise dos Riscos' },
  { key: 'k', label: 'Política da Qualidade' },
  { key: 'l', label: 'Tratamento de reclamação de cliente' },
  { key: 'm', label: 'Auditorias' },
  { key: 'n', label: 'Requisitos regulatórios aplicáveis' },
  { key: 'o', label: 'Comunicação de Autoridades' },
];

const ACOES_TOPICS = [
  { key: 'p', label: 'Melhoria do Sistema' },
  { key: 'q', label: 'Melhoria do Produto' },
  { key: 'r', label: 'Melhoria do Processo' },
  { key: 's', label: 'Requisitos do cliente' },
  { key: 't', label: 'Mudanças previstas' },
  { key: 'u', label: 'Alterações para atender requisitos regulatórios' },
  { key: 'v', label: 'Condições Climáticas' },
  { key: 'w', label: 'Necessidade de recursos' },
];

const ALL_TOPICS = [...PAUTA_TOPICS, ...ACOES_TOPICS];

const TREND_OPTIONS = [
  { value: '', label: '—' },
  { value: 'crescimento', label: 'Crescimento' },
  { value: 'retracao', label: 'Retração' },
  { value: 'manutencao', label: 'Manutenção' },
];
const TREND_LABELS = { crescimento: 'Crescimento', retracao: 'Retração', manutencao: 'Manutenção' };

const EMPTY_PARTICIPANT = { role: '', name: '', email: '' };

const emptyTopics = () => ALL_TOPICS.reduce((acc, t) => ({ ...acc, [t.key]: '' }), {});

const EMPTY_FORM = {
  realization_date: '',
  participants: [{ ...EMPTY_PARTICIPANT }],
  topics: emptyTopics(),
  topic_items: {},  // { [letter]: [description, ...] } — catalog tags shown beside the label, independent from the free-text note
  trends: [],   // [{ description, evaluation }] — rows picked from the Itens de ATA catalog
};

const FormSection = ({ title, children }) => (
  <div className="border border-gray-700 rounded-lg p-4 space-y-3">
    <h3 className="text-xs font-semibold text-green-400 uppercase tracking-wide">{title}</h3>
    {children}
  </div>
);

// One catalog picker per section (Pauta / Ações) instead of one per topic
// line: choose a registered item, choose which lettered topic it goes into,
// then insert — keeps each topic's own label/textarea exactly as before.
const CatalogInserter = ({ items, topics, onInsert }) => {
  const [itemId, setItemId] = useState('');
  const [topicKey, setTopicKey] = useState('');

  const handleInsert = () => {
    const item = items.find((i) => i.id === itemId);
    if (!item || !topicKey) return;
    onInsert(topicKey, item.description);
    setItemId('');
  };

  return (
    <div className="flex flex-wrap items-center gap-2 -mt-1 mb-1 p-2 rounded-lg bg-gray-900/40 border border-gray-700">
      <select value={itemId} onChange={(e) => setItemId(e.target.value)} className="input py-1 text-sm flex-1 min-w-[220px]">
        <option value="">Selecione um item do catálogo...</option>
        {items.map((i) => (
          <option key={i.id} value={i.id}>{i.code} — {i.description}</option>
        ))}
      </select>
      <select value={topicKey} onChange={(e) => setTopicKey(e.target.value)} className="input py-1 text-sm w-56">
        <option value="">Inserir em...</option>
        {topics.map((t) => (
          <option key={t.key} value={t.key}>{t.key}) {t.label}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={handleInsert}
        disabled={!itemId || !topicKey}
        className="flex items-center gap-1 text-sm text-green-400 hover:text-green-300 disabled:text-gray-600 disabled:cursor-not-allowed"
      >
        <Plus size={16} /> Inserir
      </button>
    </div>
  );
};

const TopManagement = () => {
  const [activeTab, setActiveTab] = useState('atas');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [ataCatalog, setAtaCatalog] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await topManagementApi.list();
      setRecords(res.data);
    } catch {
      toast.error('Erro ao carregar registros de Análise Crítica pela Direção');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    ataItemsApi.list({ is_active: true }).then((res) => setAtaCatalog(res.data || [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = async (record) => {
    try {
      const res = await topManagementApi.get(record.id);
      const full = res.data;
      setEditing(full);
      setForm({
        realization_date: full.realization_date || '',
        participants: full.participants?.length ? full.participants : [{ ...EMPTY_PARTICIPANT }],
        topics: { ...emptyTopics(), ...(full.topics || {}) },
        topic_items: full.topic_items || {},
        trends: full.trends || [],
      });
      setShowModal(true);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao carregar registro'));
    }
  };

  const openView = async (record) => {
    try {
      const res = await topManagementApi.get(record.id);
      setViewing(res.data);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao carregar registro'));
    }
  };

  const handleRequestSignature = async (idx) => {
    try {
      await topManagementApi.requestSignature(viewing.id, idx);
      toast.success('Convite de assinatura enviado por e-mail');
      const res = await topManagementApi.get(viewing.id);
      setViewing(res.data);
      load();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao enviar convite de assinatura'));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const dateError = validateDateFields([{ value: form.realization_date, label: 'Realização' }]);
    if (dateError) { toast.error(dateError); return; }

    setSaving(true);
    const payload = {
      realization_date: form.realization_date || null,
      participants: form.participants.filter((p) => p.name.trim()),
      topics: form.topics,
      topic_items: form.topic_items,
      trends: form.trends,
    };
    try {
      if (editing) {
        await topManagementApi.update(editing.id, payload);
        toast.success('Registro atualizado com sucesso');
      } else {
        await topManagementApi.create(payload);
        toast.success('Registro criado com sucesso');
      }
      setShowModal(false);
      load();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar registro'));
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async (record) => {
    setDownloadingId(record.id);
    try {
      const res = await topManagementApi.getPdf(record.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      await topManagementApi.delete(confirmDelete.id);
      toast.success('Registro excluído');
      setConfirmDelete(null);
      load();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao excluir registro'));
    }
  };

  // Participants
  const updateParticipant = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      participants: f.participants.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    }));
  };
  const addParticipant = () => setForm((f) => ({ ...f, participants: [...f.participants, { ...EMPTY_PARTICIPANT }] }));
  const removeParticipant = (idx) => setForm((f) => ({ ...f, participants: f.participants.filter((_, i) => i !== idx) }));

  // Topics (a-w)
  const updateTopic = (key, value) => setForm((f) => ({ ...f, topics: { ...f.topics, [key]: value } }));

  // Catalog tags per topic — shown as chips beside the label, independent
  // from the free-text note above (never touches `topics[key]`).
  const addTopicItem = (key, description) => {
    if (!description) return;
    setForm((f) => {
      const current = f.topic_items[key] || [];
      if (current.includes(description)) return f;
      return { ...f, topic_items: { ...f.topic_items, [key]: [...current, description] } };
    });
  };
  const removeTopicItem = (key, idx) => {
    setForm((f) => ({
      ...f,
      topic_items: { ...f.topic_items, [key]: (f.topic_items[key] || []).filter((_, i) => i !== idx) },
    }));
  };

  // Trends — rows picked from the Itens de ATA catalog (category "tendencia")
  const addTrend = (description) => {
    if (!description) return;
    setForm((f) => (
      f.trends.some((t) => t.description === description)
        ? f
        : { ...f, trends: [...f.trends, { description, evaluation: '' }] }
    ));
  };
  const updateTrendEvaluation = (idx, evaluation) => {
    setForm((f) => ({ ...f, trends: f.trends.map((t, i) => (i === idx ? { ...t, evaluation } : t)) }));
  };
  const removeTrend = (idx) => setForm((f) => ({ ...f, trends: f.trends.filter((_, i) => i !== idx) }));

  const TABS = [
    { id: 'atas', label: 'Alta Direção', icon: Crown },
    { id: 'items', label: 'Itens de ATA', icon: NotebookText },
  ];
  const TabBar = () => (
    <div className="flex gap-1 border-b border-gray-700 mb-2">
      {TABS.map((tab) => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
            activeTab === tab.id ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          <tab.icon size={16} />
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (activeTab === 'items') {
    return (
      <div className="space-y-6">
        <TabBar />
        <AtaItems />
      </div>
    );
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
    </div>
  );

  return (
    <div className="space-y-6">
      <TabBar />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Crown size={28} className="text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Alta Direção</h1>
          </div>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Nova Análise Crítica
        </button>
      </div>

      {/* List */}
      {records.length === 0 ? (
        <div className="card text-center py-20 text-gray-400">
          <Crown size={48} className="mx-auto mb-3 opacity-30" />
          <p>Nenhuma Análise Crítica pela Direção registrada</p>
          <button onClick={openCreate} className="btn-primary mt-4 mx-auto">Registrar Primeira Análise</button>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Realização</th>
                <th>Participantes</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id}>
                  <td className="font-mono text-white">{r.code}</td>
                  <td className="text-gray-400">{formatDateBR(r.realization_date)}</td>
                  <td className="text-gray-400 flex items-center gap-1">
                    <Users size={14} /> {r.participants_count}
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openView(r)} className="p-1 text-gray-400 hover:text-green-400" title="Visualizar">
                        <Eye size={18} />
                      </button>
                      <button onClick={() => openEdit(r)} className="p-1 text-gray-400 hover:text-blue-400" title="Editar">
                        <Edit size={18} />
                      </button>
                      <button onClick={() => handleDownloadPdf(r)} disabled={downloadingId === r.id} className="p-1 text-gray-400 hover:text-purple-400" title="Baixar PDF">
                        {downloadingId === r.id ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                      </button>
                      <button onClick={() => setConfirmDelete(r)} className="p-1 text-gray-400 hover:text-red-400" title="Excluir">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-4xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">{editing ? 'Editar' : 'Nova'} Análise Crítica pela Direção</h2>
                <p className="text-xs text-gray-500">{FORM_CODE} Rev.{FORM_REVISION} — Ref.: {FORM_REF}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <FormSection title="Identificação">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Realização</label>
                  <input
                    type="date"
                    value={form.realization_date}
                    onChange={(e) => setForm({ ...form, realization_date: e.target.value })}
                    className="input w-full max-w-xs"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
              </FormSection>

              <FormSection title="Participantes">
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Função</th>
                        <th>Nome</th>
                        <th>E-mail (para assinatura digital)</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.participants.map((p, idx) => (
                        <tr key={idx}>
                          <td>
                            <input type="text" value={p.role} onChange={(e) => updateParticipant(idx, 'role', e.target.value)} className="input w-full" />
                          </td>
                          <td>
                            <input type="text" value={p.name} onChange={(e) => updateParticipant(idx, 'name', e.target.value)} className="input w-full" />
                          </td>
                          <td>
                            <input type="email" value={p.email || ''} onChange={(e) => updateParticipant(idx, 'email', e.target.value)} className="input w-full" placeholder="nome@email.com" />
                          </td>
                          <td>
                            <button type="button" onClick={() => removeParticipant(idx)} className="p-1 text-gray-400 hover:text-red-400" title="Remover">
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button type="button" onClick={addParticipant} className="flex items-center gap-1 text-sm text-green-400 hover:text-green-300 mt-2">
                  <Plus size={16} /> Adicionar Participante
                </button>
              </FormSection>

              <FormSection title="Pauta (a-o)">
                {ataCatalog.some((i) => i.category === 'pauta') && (
                  <CatalogInserter
                    items={ataCatalog.filter((i) => i.category === 'pauta')}
                    topics={PAUTA_TOPICS}
                    onInsert={addTopicItem}
                  />
                )}
                <div className="space-y-4">
                  {PAUTA_TOPICS.map((t) => (
                    <React.Fragment key={t.key}>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <label className="block text-sm font-medium text-gray-300">{t.key}) {t.label}</label>
                          {(form.topic_items[t.key] || []).map((tag, idx) => (
                            <span key={idx} className="flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs bg-green-500/20 text-green-300">
                              {tag}
                              <button type="button" onClick={() => removeTopicItem(t.key, idx)} className="hover:text-white" title="Remover">
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                        <textarea
                          value={form.topics[t.key] || ''}
                          onChange={(e) => updateTopic(t.key, e.target.value)}
                          className="input w-full"
                          rows={2}
                        />
                      </div>
                      {t.key === 'e' && (
                        <div className="ml-4 border-l-2 border-gray-700 pl-4">
                          <p className="text-xs text-gray-400 mb-2">Subtabela de Tendências</p>
                          {form.trends.length > 0 && (
                            <div className="table-container mb-2">
                              <table className="table">
                                <thead>
                                  <tr><th></th><th>Item</th><th>Avaliação</th><th></th></tr>
                                </thead>
                                <tbody>
                                  {form.trends.map((tr, idx) => (
                                    <tr key={idx}>
                                      <td className="text-gray-500">{idx + 1}</td>
                                      <td className="text-gray-300">{tr.description}</td>
                                      <td>
                                        <select
                                          value={tr.evaluation || ''}
                                          onChange={(e) => updateTrendEvaluation(idx, e.target.value)}
                                          className="input w-full"
                                        >
                                          {TREND_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                          ))}
                                        </select>
                                      </td>
                                      <td>
                                        <button type="button" onClick={() => removeTrend(idx)} className="p-1 text-gray-400 hover:text-red-400" title="Remover">
                                          <Trash2 size={16} />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {ataCatalog.some((i) => i.category === 'tendencia') ? (
                            <select
                              value=""
                              onChange={(e) => addTrend(e.target.value)}
                              className="input py-1 text-sm w-64"
                              title="Adicionar item cadastrado"
                            >
                              <option value="">+ Adicionar item cadastrado...</option>
                              {ataCatalog.filter((i) => i.category === 'tendencia').map((i) => (
                                <option key={i.id} value={i.description}>{i.code} — {i.description}</option>
                              ))}
                            </select>
                          ) : (
                            <p className="text-xs text-gray-500">
                              Nenhum item de Tendência cadastrado. Cadastre em <span className="text-green-400">Itens de ATA</span>.
                            </p>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </FormSection>

              <FormSection title="Ações (p-w)">
                {ataCatalog.some((i) => i.category === 'acao') && (
                  <CatalogInserter
                    items={ataCatalog.filter((i) => i.category === 'acao')}
                    topics={ACOES_TOPICS}
                    onInsert={addTopicItem}
                  />
                )}
                <div className="space-y-4">
                  {ACOES_TOPICS.map((t) => (
                    <div key={t.key}>
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <label className="block text-sm font-medium text-gray-300">{t.key}) {t.label}</label>
                        {(form.topic_items[t.key] || []).map((tag, idx) => (
                          <span key={idx} className="flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs bg-green-500/20 text-green-300">
                            {tag}
                            <button type="button" onClick={() => removeTopicItem(t.key, idx)} className="hover:text-white" title="Remover">
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                      <textarea
                        value={form.topics[t.key] || ''}
                        onChange={(e) => updateTopic(t.key, e.target.value)}
                        className="input w-full"
                        rows={2}
                      />
                    </div>
                  ))}
                </div>
              </FormSection>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary" disabled={saving}>Cancelar</button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* View Modal */}
      {viewing && (
        <>
          <div className="modal-backdrop" onClick={() => setViewing(null)} />
          <div className="modal-content p-6 max-w-3xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">{FORM_CODE} — Ata de Análise Crítica pela Direção</h2>
                <p className="text-xs text-gray-500">Cód.: {FORM_CODE} · Rev.: {FORM_REVISION} · {viewing.code}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownloadPdf(viewing)}
                  disabled={downloadingId === viewing.id}
                  className="btn-secondary text-sm flex items-center gap-2"
                >
                  {downloadingId === viewing.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  Baixar PDF
                </button>
                <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <p className="text-gray-400 text-sm">Realização</p>
                <p className="text-white font-medium">{formatDateBR(viewing.realization_date)}</p>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-300 mb-2">Participantes</p>
                {viewing.participants?.length ? (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr><th>Função</th><th>Nome</th><th>Assinatura Digital</th><th></th></tr>
                      </thead>
                      <tbody>
                        {viewing.participants.map((p, idx) => (
                          <tr key={idx}>
                            <td className="text-gray-400">{p.role || '-'}</td>
                            <td className="text-white">{p.name}</td>
                            <td>
                              {p.signed ? (
                                <div className="flex items-center gap-2">
                                  <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">
                                    <CheckCircle2 size={12} /> Assinado {p.signed_at ? new Date(p.signed_at).toLocaleString('pt-BR') : ''}
                                  </span>
                                  {p.signature_image && (
                                    <img src={p.signature_image} alt="Assinatura" style={{ height: '28px', background: '#fff', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }} />
                                  )}
                                </div>
                              ) : p.email ? (
                                <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400">
                                  <Clock size={12} /> Pendente
                                </span>
                              ) : (
                                <span className="text-xs text-gray-500">Sem e-mail cadastrado</span>
                              )}
                            </td>
                            <td>
                              {!p.signed && p.email && (
                                <button
                                  onClick={() => handleRequestSignature(idx)}
                                  className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                                  title="Enviar convite de assinatura por e-mail"
                                >
                                  <Send size={14} /> Enviar
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="text-sm text-gray-500">Nenhum participante registrado</p>}
              </div>

              {PAUTA_TOPICS.map((t) => (
                <React.Fragment key={t.key}>
                  {(viewing.topics?.[t.key] || viewing.topic_items?.[t.key]?.length > 0) && (
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-sm font-semibold text-gray-300">{t.key}) {t.label}</p>
                        {(viewing.topic_items?.[t.key] || []).map((tag, idx) => (
                          <span key={idx} className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-300">{tag}</span>
                        ))}
                      </div>
                      {viewing.topics?.[t.key] && (
                        <p className="text-sm text-gray-300 bg-gray-700/40 rounded p-3 whitespace-pre-wrap">{viewing.topics[t.key]}</p>
                      )}
                    </div>
                  )}
                  {t.key === 'e' && (viewing.trends || []).length > 0 && (
                    <div className="ml-4 border-l-2 border-gray-700 pl-4">
                      <p className="text-xs text-gray-400 mb-2">Subtabela de Tendências</p>
                      <div className="table-container">
                        <table className="table">
                          <thead>
                            <tr><th>Item</th><th>Avaliação</th></tr>
                          </thead>
                          <tbody>
                            {viewing.trends.map((tr, idx) => (
                              <tr key={idx}>
                                <td className="text-gray-300">{tr.description}</td>
                                <td className="text-white">{TREND_LABELS[tr.evaluation] || tr.evaluation || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}

              {ACOES_TOPICS.map((t) => (
                (viewing.topics?.[t.key] || viewing.topic_items?.[t.key]?.length > 0) ? (
                  <div key={t.key}>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-sm font-semibold text-gray-300">{t.key}) {t.label}</p>
                      {(viewing.topic_items?.[t.key] || []).map((tag, idx) => (
                        <span key={idx} className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-300">{tag}</span>
                      ))}
                    </div>
                    {viewing.topics?.[t.key] && (
                      <p className="text-sm text-gray-300 bg-gray-700/40 rounded p-3 whitespace-pre-wrap">{viewing.topics[t.key]}</p>
                    )}
                  </div>
                ) : null
              ))}

              {!ALL_TOPICS.some((t) => viewing.topics?.[t.key] || viewing.topic_items?.[t.key]?.length > 0) && (
                <p className="text-sm text-gray-500">Nenhum item de pauta preenchido</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-500/20">
                <Trash2 size={22} className="text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Confirmar Exclusão</h3>
            </div>
            <p className="text-gray-400 text-sm">
              Tem certeza que deseja excluir o registro <span className="text-white font-medium">{confirmDelete.code}</span>?
              Esta ação não poderá ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="btn-secondary">Voltar</button>
              <button
                onClick={handleConfirmDelete}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
              >
                <Trash2 size={16} /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopManagement;
